// features/email-tracking/email-tracking.service.ts
//
// Data layer for the invoice email-status timeline (Sent / Delivered / Opened).
//
// Backend: GET /SendGridWebhook/invoice/{invoiceId} (auth: Bearer JWT, role
// Employee — same as the invoice list). `invoiceId` is the invoice's internal
// numeric id (InvoiceItem.id), NOT the user-facing invoiceNumber. Returns an
// EmailTrackingStatusVM (raw SendGrid events, newest-first).
//
// This service adapts that raw response into the EmailTrackingDetails shape the
// modal consumes (fixed Sent -> Delivered -> Opened steps + a furthest status).
// The last three timeline nodes (Paid / Salary Created / Salary Paid) are NOT
// part of this call — the modal derives them from the invoice row's status flags.
//
// The v2 apiClient is used (has 401 handling + bearer-token injection).

import { get } from '../../api/api.service';
import {
    EmailStatus,
    EmailTrackingDetails,
    EmailTrackingEvent,
    EmailTrackingStatusVM,
    RawEmailTrackingEvent,
} from './email-tracking.types';

// ---------------------------------------------------------------------------
// Adapter: raw SendGrid response -> EmailTrackingDetails (email steps only)
// ---------------------------------------------------------------------------

/** The three positional email steps, always in Sent -> Delivered -> Opened order. */
const STEP_TYPES: EmailTrackingEvent['type'][] = ['sent', 'delivered', 'opened'];

const norm = (event: string): string => (event ?? '').trim().toLowerCase();

/** First event (any position) whose raw name matches one of `names`, or undefined. */
const findEvent = (
    events: RawEmailTrackingEvent[],
    names: string[]
): RawEmailTrackingEvent | undefined => events.find((e) => names.includes(norm(e.event)));

const hasEvent = (events: RawEmailTrackingEvent[], names: string[]): boolean =>
    events.some((e) => names.includes(norm(e.event)));

/**
 * Furthest email stage reached, derived by precedence over the WHOLE event list
 * (not just the newest event). Caps at `opened`; bounce/dropped/spamreport map
 * to the failure statuses; opt-out events imply the mail was delivered.
 */
const deriveStatus = (events: RawEmailTrackingEvent[]): EmailStatus => {
    if (events.length === 0) return 'not_sent';
    if (hasEvent(events, ['open', 'click'])) return 'opened';
    if (hasEvent(events, ['delivered'])) return 'delivered';
    if (hasEvent(events, ['bounce'])) return 'bounced';
    if (hasEvent(events, ['dropped', 'spamreport'])) return 'failed';
    if (hasEvent(events, ['processed', 'deferred'])) return 'sent';
    if (hasEvent(events, ['unsubscribe', 'group_unsubscribe', 'group_resubscribe'])) return 'delivered';
    return 'sent';
};

/**
 * Map the backend EmailTrackingStatusVM into the EmailTrackingDetails shape the
 * modal already consumes. Fills the three positional email steps with the
 * timestamp of the matching raw event (or null when that step has not occurred).
 */
export const mapEmailTrackingResponse = (vm: EmailTrackingStatusVM): EmailTrackingDetails => {
    const events = Array.isArray(vm?.events) ? vm.events : [];
    const status = deriveStatus(events);

    const processedAt = findEvent(events, ['processed'])?.createdDate ?? vm?.createdDate ?? null;
    const deliveredAt = findEvent(events, ['delivered'])?.createdDate ?? null;
    const openedAt = findEvent(events, ['open', 'click'])?.createdDate ?? null;
    const stepTimestamp: Record<EmailTrackingEvent['type'], string | null> = {
        sent: processedAt,
        delivered: deliveredAt,
        opened: openedAt,
    };

    const timelineEvents: EmailTrackingEvent[] = STEP_TYPES.map((type) => ({
        type,
        timestamp: stepTimestamp[type],
    }));

    return {
        invoiceNumber: vm?.invoiceNumber != null ? String(vm.invoiceNumber) : '',
        status,
        sentTo: vm?.trackedEmailAddress ?? '',
        sentOn: processedAt,
        events: timelineEvents,
    };
};

/** The details object for an invoice that has never had a tracked email (404). */
const notSentDetails = (): EmailTrackingDetails => ({
    invoiceNumber: '',
    status: 'not_sent',
    sentTo: '',
    sentOn: null,
    events: STEP_TYPES.map((type) => ({ type, timestamp: null })),
});

// ---------------------------------------------------------------------------
// Service API
// ---------------------------------------------------------------------------

/**
 * Fetch the email-status timeline for one invoice by its internal numeric id.
 * A 404 means the invoice has never had an email sent (no tracking row) and is
 * treated as a valid "not sent yet" state, not an error.
 */
export const getEmailTrackingApi = async (
    invoiceId: number
): Promise<EmailTrackingDetails> => {
    try {
        const raw = await get(`/SendGridWebhook/invoice/${invoiceId}`);
        // Handle both a bare EmailTrackingStatusVM and the shared
        // { statusCode, message, response } envelope other endpoints use.
        const vm = (raw?.response ?? raw) as EmailTrackingStatusVM;
        return mapEmailTrackingResponse(vm);
    } catch (err: any) {
        if (err?.response?.status === 404) {
            return notSentDetails();
        }
        throw err;
    }
};
